/**
 * Órdenes de Taller — pantalla unificada del módulo de taller.
 *
 * La vista principal son los eventos/órdenes de taller embebidos (ares9015:
 * órdenes abiertas y cerradas por fechas, detalle de gastos y eliminación de
 * órdenes abiertas). Tarjetas de acción:
 *   - "Recepción de motor" (ares9010): abre la orden de reparación.
 *   - "Salida de motor" (ares9020): cierra la orden, registra el pago del
 *     cliente y el destino del vehículo.
 *   - "Reporte de gastos" (ares9025): gastos por fechas/sucursal con export
 *     (diálogo con barra Volver).
 *
 * Sustituye las entradas de menú de entradataller.js, salidataller.js,
 * eventostaller.js y reportetaller.js; los cuatro se reutilizan.
 */
import React, { useState } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Dialog from "@mui/material/Dialog";
import BuildOutlinedIcon from "@mui/icons-material/BuildOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import { useMaterialUIController } from "context";
import { FP, PageHeader, ActionCard, BarraVolver } from "components/financepro";
import { tienePermisosModulo } from "lib/seguridad/permisosPorModulo";
import EntradaTaller from "modules/taller/entradataller";
import SalidaTaller from "modules/taller/salidataller";
import EventosTaller from "modules/taller/eventostaller";
import ReporteTaller from "modules/taller/reportetaller";
import useAccionInicial from "lib/useAccionInicial";

export default function OrdenesTaller() {
  const [controller] = useMaterialUIController();
  const { roleDetalles } = controller;

  // Permisos por programa ARES (el backend re-valida en permisos.py).
  const puedeEntrada = tienePermisosModulo(roleDetalles, "ares9010");
  const puedeSalida = tienePermisosModulo(roleDetalles, "ares9020");
  const puedeEventos = tienePermisosModulo(roleDetalles, "ares9015");
  const puedeReporte = tienePermisosModulo(roleDetalles, "ares9025");

  const [entradaOpen, setEntradaOpen] = useState(false);
  const [salidaOpen, setSalidaOpen] = useState(false);
  const [reporteOpen, setReporteOpen] = useState(false);

  // Enlace profundo desde el buscador global (?accion=...).
  useAccionInicial({
    recepcion: { permitido: puedeEntrada, abrir: () => setEntradaOpen(true) },
    salida: { permitido: puedeSalida, abrir: () => setSalidaOpen(true) },
    reporte: { permitido: puedeReporte, abrir: () => setReporteOpen(true) },
  });

  // Incrementarlo recarga el listado embebido (tras una entrada/salida).
  const [refreshKey, setRefreshKey] = useState(0);
  const refrescar = () => setRefreshKey((k) => k + 1);

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <PageHeader
        title="Órdenes de Taller"
        subtitle="Recepción, salida y seguimiento de las órdenes de taller en un solo lugar"
      />

      <Box sx={{ p: { xs: 2, md: 3 } }}>
        {(puedeEntrada || puedeSalida || puedeReporte) && (
          <Grid container spacing={2.5} sx={{ mb: 3 }}>
            {puedeEntrada && (
              <Grid item xs={12} md={4}>
                <ActionCard
                  icon={BuildOutlinedIcon}
                  titulo="Recepción de motor"
                  descripcion="Registra la entrada del vehículo y abre la orden"
                  color={FP.color.success}
                  onClick={() => setEntradaOpen(true)}
                />
              </Grid>
            )}
            {puedeSalida && (
              <Grid item xs={12} md={4}>
                <ActionCard
                  icon={LogoutOutlinedIcon}
                  titulo="Salida de motor"
                  descripcion="Cierra la orden: pago del cliente y destino del vehículo"
                  color={FP.color.accentBlue}
                  onClick={() => setSalidaOpen(true)}
                />
              </Grid>
            )}
            {puedeReporte && (
              <Grid item xs={12} md={4}>
                <ActionCard
                  icon={AssessmentOutlinedIcon}
                  titulo="Reporte de gastos"
                  descripcion="Gastos de taller por fechas y sucursal, con export"
                  color={FP.color.info}
                  onClick={() => setReporteOpen(true)}
                />
              </Grid>
            )}
          </Grid>
        )}

        {/* Vista principal: órdenes de taller (detalle y eliminación) */}
        {puedeEventos && <EventosTaller embedded refreshToken={refreshKey} />}
      </Box>

      {/* Recepción de motor (formulario a pantalla completa) */}
      <Dialog fullScreen open={entradaOpen} onClose={() => setEntradaOpen(false)}>
        <EntradaTaller embedded onExit={() => { setEntradaOpen(false); refrescar(); }} onSaved={refrescar} />
      </Dialog>

      {/* Salida de motor (formulario a pantalla completa) */}
      <Dialog fullScreen open={salidaOpen} onClose={() => setSalidaOpen(false)}>
        <SalidaTaller embedded onExit={() => { setSalidaOpen(false); refrescar(); }} onSaved={refrescar} />
      </Dialog>

      {/* Reporte de gastos (pantalla existente, con barra Volver) */}
      <Dialog fullScreen open={reporteOpen} onClose={() => setReporteOpen(false)}>
        <BarraVolver onExit={() => setReporteOpen(false)} />
        <ReporteTaller />
      </Dialog>
    </Box>
  );
}
